import { NgFor, NgIf } from '@angular/common';
import { Component, Input, Optional } from '@angular/core';
import { TypographyComponent } from 'components';
import { ReportBlockData } from '../../../../services/report-blocks.service';
import { ReportLanguageService } from '../../../../services/report-language.service';
import { reportMessage } from '../../../../services/report-messages';
import { normalizeThemeColor } from '../../../../services/report-preview-utils';

/** One row of the timeline, prepared once so the template only reads fields. */
interface AlertTimelineEntry {
	/** Its day, printed on the first of that day's entries only. */
	day: string | null;
	time: string;
	rule: string;
	fired: string;
	/** "12 fires" for an hour of repeats; null for a fire on its own. */
	fires: string | null;
}

/**
 * Alerts fired drawn down a line of time, newest first: one dot per row of
 * the section — a rule's fires within one UTC hour — with its time, the rule
 * and what fired.
 *
 * The rows are the backend's, in its order and its words (`blocks/alerts.py`):
 * the day and the time come written in the report's language, so the editor,
 * the PDF, the shared page and present mode print the same text. Only the
 * count is said here, as the table prints it as a bare number.
 */
@Component({
	standalone: true,
	selector: 'app-report-alerts-timeline',
	imports: [NgFor, NgIf, TypographyComponent],
	templateUrl: './report-alerts-timeline.component.html',
	styleUrls: ['./report-alerts-timeline.component.scss']
})
export class ReportAlertsTimelineComponent {
	@Input() set block(value: ReportBlockData | null) {
		this.entries = this.buildEntries(value);
	}

	/** The report's accent for the dots; null keeps its theme's own. */
	@Input() set themeColor(value: string | null) {
		this.accent = normalizeThemeColor(value);
	}

	entries: AlertTimelineEntry[] = [];
	accent: string | null = null;

	constructor(
		@Optional() private reportLanguage: ReportLanguageService | null
	) {}

	trackByIndex(index: number): number {
		return index;
	}

	private buildEntries(block: ReportBlockData | null): AlertTimelineEntry[] {
		const language = this.reportLanguage?.language;
		let previousDay: string | null = null;
		return (block?.rows ?? []).map(row => {
			const day = String(row['day'] ?? '');
			const fires = Number(row['fires'] ?? 0);
			const entry: AlertTimelineEntry = {
				day: day !== previousDay ? day : null,
				time: String(row['time'] ?? ''),
				rule: String(row['rule'] ?? ''),
				fired: String(row['fired'] ?? ''),
				fires:
					fires > 1
						? reportMessage(language, 'alerts.fires', {
								count: fires
						  })
						: null
			};
			previousDay = day;
			return entry;
		});
	}
}
